'use client'

import { useEffect, useState } from 'react'
import { UsageBadge } from './usage-badge'

/**
 * Header-mounted billing badge.
 *
 * Renders nothing unless the authenticated user is on the free plan;
 * otherwise fetches current-month usage once on mount and surfaces
 * peak percentage (API calls vs. prompts) as a clickable pill.
 *
 * Intentionally client-side and lazy: does not block page render and
 * costs one fetch per navigation (the browser will cache the API
 * response per tab).
 */
export function HeaderBillingBadge({ plan }: { plan: string | undefined }) {
  const [percentage, setPercentage] = useState<number | null>(null)

  useEffect(() => {
    if (plan !== 'free') return
    let cancelled = false
    ;(async () => {
      try {
        const res = await fetch('/api/v1/billing/usage', { credentials: 'include' })
        if (!res.ok) return
        const body = await res.json() as {
          data?: {
            current_month?: {
              percentages?: { api_calls?: number; prompts?: number }
            }
          }
        }
        const pct = body.data?.current_month?.percentages
        if (cancelled || !pct) return
        setPercentage(Math.max(pct.api_calls ?? 0, pct.prompts ?? 0))
      } catch {
        // Network / parse failures leave the badge hidden.
      }
    })()
    return () => { cancelled = true }
  }, [plan])

  if (plan !== 'free' || percentage === null) return null
  return <UsageBadge percentage={percentage} />
}
